import { useAuth } from "@assetwatch/auth";
import { useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";
import {
  buildPendoAccount,
  buildPendoVisitor,
  clearPendoSession,
  getPendoApiKey,
  initializePendo,
  isLocalhost,
  teardownPendo,
  trackPendoPageView,
} from "../lib/pendo";
import { useWorkContextQuery } from "../queries/useWorkContextQuery";

export function PendoSync() {
  const { user } = useAuth();
  const { pathname, requestedCustomerId } = useRouterState({
    select: (state) => {
      const search = state.location.search as {
        externalCustomerId?: string;
        customer?: string;
      };

      return {
        pathname: state.location.pathname,
        requestedCustomerId: search.externalCustomerId ?? search.customer,
      };
    },
  });
  const { data } = useWorkContextQuery();
  const apiKey = getPendoApiKey(import.meta.env.VITE_PENDO_API_KEY);
  const customers = data?.customers ?? [];
  const selectedCustomer =
    customers.find((customer) => customer.id === requestedCustomerId) ??
    customers[0];
  const sub = user?.sub;
  const account = buildPendoAccount(selectedCustomer);
  const accountId = account?.id;
  const accountName = account?.name;
  const rolesKey = user ? buildPendoVisitor(user).roles.join(",") : undefined;
  // Wait for a successful customer context so the account is known at initialize.
  const isEnabled = Boolean(apiKey && user && data && !isLocalhost());

  // Declared first so the session is cleared before the agent is torn down on sign-out.
  useEffect(() => clearPendoSession, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-initialize only when the signed-in identity changes, not on every user object refresh
  useEffect(() => {
    if (!apiKey || !user || !isEnabled) {
      return;
    }

    initializePendo(apiKey, buildPendoVisitor(user), account);

    return teardownPendo;
  }, [apiKey, isEnabled, sub, accountId, accountName, rolesKey]);

  // Runs after the effect above, so the agent exists when a page view is sent.
  // biome-ignore lint/correctness/useExhaustiveDependencies: identity keys re-send the page view after a re-initialize
  useEffect(() => {
    if (isEnabled) {
      trackPendoPageView(pathname);
    }
  }, [isEnabled, pathname, sub, accountId, accountName, rolesKey]);

  return null;
}
